import {
    getOptionalSurvey,
    submitMoreInfoAnswers,
} from '../actions/surveyActions';
import InformationPageTemplate from '../components/InformationPageTemplate';
import {
    MoreInfoDropdown,
    MoreInfoSearchableDropdown,
} from '../components/MoreInfoQuestion';
import { MoreInfoAnswerObject } from '../model/models';
import { State } from '../redux-state';
import { colors } from '../styles';
import { authenticatedGetJson } from '../utils/requests';
import React, { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useDispatch, useSelector } from 'react-redux';
import { useLocation, useNavigate, useParams } from 'react-router-dom';
import styled from 'styled-components';

const backendUrl = process.env.REACT_APP_BACKEND_URL;

const Subtitle = styled.div`
    color: ${colors.libertyBlue};
    margin-bottom: 50px;
`;

const Questions = styled.div`
    > *:not(.domain) {
        margin-bottom: 50px;
    }
    .domain {
        margin-bottom: 25px;
    }
`;

const getMoreInfoPageContent = (
    answersObject: MoreInfoAnswerObject,
    setAnswersObject: (arg0: any) => void,
    disabled: boolean,
) => {
    const { t, i18n } = useTranslation(['Informations']);
    const { survey_id } = useParams();
    const [client_name, setClientName] = useState<any>('');
    useEffect(() => {
        async function fetchConfig() {
            const response = await fetch(`${backendUrl}/get_client_info`, {
                headers: {
                    'Survey-Id': survey_id || '',
                },
            });
            const data = await response.json();
            setClientName(data.rows[0].client);
        }
        fetchConfig();
    }, []);

    const [roles, setRoles] = useState<any>([]);
    useEffect(() => {
        async function fetchRoles() {
            const response = await authenticatedGetJson(
                `${backendUrl}/get_roles`,
                survey_id,
            );
            setRoles(response);
            // Convert any existing role name to role ID when roles are loaded
            if (answersObject.role && response.length > 0) {
                const existingRole = response.find(
                    (r: any) => r.role === answersObject.role,
                );
                if (existingRole) {
                    setAnswersObject({
                        ...answersObject,
                        role: String(existingRole.id),
                    });
                }
            }
        }
        fetchRoles();
    }, [i18n.language]);

    const questions = t('questions') as unknown as any[];

    const new_subtitle = client_name.replace(/_/g, ' ');
    return (
        <>
            {client_name && (
                <React.Fragment>
                    <Subtitle>
                        {t('subtitle', { survey_id: new_subtitle })}
                    </Subtitle>
                    <Questions>
                        {questions.map((question: any, index: any) => {
                            const currentAnswer = answersObject[question.key];
                            switch (question.type) {
                                case 'dropdown':
                                    return (
                                        <MoreInfoDropdown
                                            key={`more-info-question-${index}`}
                                            keyString={`more-info-question-${index}`}
                                            title={question.title}
                                            answers={question.answers}
                                            placeholder={question.placeholder}
                                            currentAnswer={currentAnswer}
                                            disabled={disabled}
                                            setCurrentAnswer={(
                                                answer: string,
                                            ) =>
                                                setAnswersObject({
                                                    ...answersObject,
                                                    [question.key]: answer,
                                                })
                                            }
                                        />
                                    );
                                case 'freetext':
                                    return (
                                        <MoreInfoSearchableDropdown
                                            key={`more-info-question-${index}`}
                                            keyString={`more-info-question-${index}`}
                                            title={question.title}
                                            defaultValue={
                                                question.defaultValue || ''
                                            }
                                            answers={roles.map(
                                                (obj: any) => obj.role,
                                            )}
                                            placeholder={question.placeholder}
                                            currentAnswer={
                                                roles.find(
                                                    (r: any) =>
                                                        String(r.id) ===
                                                        currentAnswer,
                                                )?.role || currentAnswer
                                            }
                                            noResultsText={
                                                question.noResultsText || ''
                                            }
                                            searchPlaceholder={
                                                question.searchPlaceholder || ''
                                            }
                                            returnIndex={false}
                                            disabled={disabled}
                                            setCurrentAnswer={(
                                                answer: string,
                                            ) => {
                                                const selectedRole = roles.find(
                                                    (r: any) =>
                                                        r.role === answer,
                                                );
                                                setAnswersObject({
                                                    ...answersObject,
                                                    [question.key]: selectedRole
                                                        ? String(
                                                              selectedRole.id,
                                                          )
                                                        : answer,
                                                });
                                            }}
                                        />
                                    );
                            }
                        })}
                    </Questions>
                </React.Fragment>
            )}
        </>
    );
};

const MoreInfoPage: React.FC = (): React.ReactElement => {
    const moreInfoAnswers = useSelector(
        (state: State): MoreInfoAnswerObject =>
            state.surveyState.userInfos.moreInfo,
    );

    const [answers, setAnswers] = useState(
        moreInfoAnswers as any as MoreInfoAnswerObject,
    );

    const dispatch = useDispatch();
    const { survey_id, uid } = useParams();

    useEffect(() => {
        setAnswers(moreInfoAnswers as any as MoreInfoAnswerObject);
    }, [uid, moreInfoAnswers]);

    const submitAnswers = () => {
        dispatch(submitMoreInfoAnswers(answers, survey_id, uid, performNext));
    };

    const { t } = useTranslation(['Informations']);

    const isManagerFlow = sessionStorage.getItem('isManagerFlow') === 'true';
    const managerUid = sessionStorage.getItem('managerUid');

    let emailAuth: any;
    const navigate = useNavigate();
    const location = useLocation();
    const stateVar = location.state;
    if (!stateVar || !stateVar.emailAuth) {
        const storedEmailAuth = sessionStorage.getItem('emailAuth');
        if (storedEmailAuth) {
            emailAuth = storedEmailAuth;
        } else {
            navigate(`/${survey_id}/${uid}`, { replace: true });
        }
    } else {
        emailAuth = stateVar.emailAuth;
    }

    const performNext = async (success: boolean) => {
        if (success) {
            await dispatch(getOptionalSurvey(survey_id, uid, true));
            const helpVisited =
                sessionStorage.getItem('helpVisited') === 'true';
            const nextPath = helpVisited ? 'mandatory-welcome' : 'help';
            navigate(`/${survey_id}/${uid}/${nextPath}`, {
                state: {
                    levels_state: null,
                    emailAuth: emailAuth,
                },
            });
        }
    };

    const getPreviousPath = (): string => {
        if (isManagerFlow && managerUid) {
            return `/${survey_id}/${managerUid}/reportees`;
        }
        return `/${survey_id}/${uid}/${t('previousViewSlug')}`;
    };

    return (
        <InformationPageTemplate
            title={t('title') || ''}
            tagline={t('tagline') || ''}
            content={getMoreInfoPageContent(answers, setAnswers, false)}
            nextText={t('nextText') || ''}
            previousText={t('previousText') || ''}
            previousPath={getPreviousPath()}
            middleImage={t('middleImage') || ''}
            canOverflow={true}
            shouldNavForward={(t('questions') as unknown as any[])
                .filter((q: any) => !q.optional)
                .every((q: any) => !!moreInfoAnswers[q.key])}
            buttonDisabled={
                !(t('questions') as unknown as any[])
                    .filter((q: any) => !q.optional)
                    .every((q: any) => !!answers[q.key])
            }
            onButtonClick={submitAnswers}
            logoImage="/talentbuilderlogo.png"
            emailAuth={emailAuth}
        />
    );
};

export default MoreInfoPage;
